import { CheckIcon } from "@heroicons/react/24/outline"
import React, { useState, useEffect } from "react"

import { Button } from "../../../../components/shared/Buttons"
import { formatFontFamily } from "../../utils/fontUtils"

// Google Fonts URL for all fonts used in the font selector
const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Work+Sans:wght@300;400;600;700&family=Inter:wght@300;400;600;700&family=Libre+Franklin:wght@300;400;600;700&family=Poppins:wght@400;600;700&family=Raleway:wght@300;400;600;700&family=Montserrat:wght@400;600;700&family=Lato:wght@300;400;600;700&family=Open+Sans:wght@400;600;700&family=Source+Sans+Pro:wght@300;400;600;700&family=Arima+Madurai:wght@400;600;700&family=Mulish:wght@300;400;600;700&family=Cabin:wght@300;400;600;700&family=EB+Garamond:wght@400;600;700&family=Spline+Sans:wght@300;400;600;700&family=Jost:wght@400;600;700&family=Nunito:wght@400;600;700&family=Nunito+Sans:wght@300;400;600;700&display=swap"

const FontsForm = ({ theme, onUpdate, onClose }) => {
  const fontPairs = [
    {
      id: "playfair-work",
      primary: "Playfair Display",
      secondary: "Work Sans",
      primaryFont: "Playfair Display, serif",
      secondaryFont: "Work Sans, sans-serif"
    },
    {
      id: "inter-libre",
      primary: "Inter",
      secondary: "Libre Franklin",
      primaryFont: "Inter, sans-serif",
      secondaryFont: "Libre Franklin, sans-serif"
    },
    {
      id: "poppins-raleway",
      primary: "Poppins",
      secondary: "Raleway",
      primaryFont: "Poppins, sans-serif",
      secondaryFont: "Raleway, sans-serif"
    },
    {
      id: "montserrat-lato",
      primary: "Montserrat",
      secondary: "Lato",
      primaryFont: "Montserrat, sans-serif",
      secondaryFont: "Lato, sans-serif"
    },
    {
      id: "opensans-sourcesans",
      primary: "Open Sans",
      secondary: "Source Sans Pro",
      primaryFont: "Open Sans, sans-serif",
      secondaryFont: "Source Sans Pro, sans-serif"
    },
    {
      id: "arima-madurai",
      primary: "Arima Madurai",
      secondary: "Mulish",
      primaryFont: "Arima Madurai, sans-serif",
      secondaryFont: "Mulish, sans-serif"
    },
    {
      id: "cabin-cabin",
      primary: "Cabin",
      secondary: "Cabin",
      primaryFont: "Cabin, sans-serif",
      secondaryFont: "Cabin, sans-serif"
    },
    {
      id: "ebgaramond-splinesans",
      primary: "EB Garamond",
      secondary: "Spline Sans",
      primaryFont: "EB Garamond, serif",
      secondaryFont: "Spline Sans, sans-serif"
    },
    {
      id: "jost-inter",
      primary: "Jost",
      secondary: "Inter",
      primaryFont: "Jost, sans-serif",
      secondaryFont: "Inter, sans-serif"
    },
    {
      id: "nunito-nunitosans",
      primary: "Nunito",
      secondary: "Nunito Sans",
      primaryFont: "Nunito, sans-serif",
      secondaryFont: "Nunito Sans, sans-serif"
    }
  ]

  // Function to find the font pair ID based on current theme fonts
  const findFontPairId = (headingFont, paragraphFont) => {
    const pair = fontPairs.find((pair) => pair.primaryFont === headingFont && pair.secondaryFont === paragraphFont)
    return pair ? pair.id : "playfair-work" // Default fallback
  }

  // Initialize selected font pair from current theme
  const initialFontPairId = findFontPairId(theme?.fonts?.heading, theme?.fonts?.paragraph)
  const [selectedFontPair, setSelectedFontPair] = useState(initialFontPairId)

  // Update selected font pair when theme changes
  useEffect(() => {
    const newFontPairId = findFontPairId(theme?.fonts?.heading, theme?.fonts?.paragraph)
    setSelectedFontPair(newFontPairId)
  }, [theme?.fonts?.heading, theme?.fonts?.paragraph])

  // Inject Google Fonts stylesheet into the document head
  useEffect(() => {
    // Check if link already exists
    const existingLink = document.querySelector(`link[href="${GOOGLE_FONTS_URL}"]`)
    if (existingLink) return

    // Create and inject link tag
    const link = document.createElement("link")
    link.href = GOOGLE_FONTS_URL
    link.rel = "stylesheet"
    link.crossOrigin = "anonymous"
    document.head.appendChild(link)

    // Cleanup function
    return () => {
      // Don't remove on unmount as other components might need it
    }
  }, [])

  const handleFontSelect = (fontPairId) => {
    setSelectedFontPair(fontPairId)

    // Update the renderer immediately with temporary preview
    const selectedPair = fontPairs.find((pair) => pair.id === fontPairId)
    onUpdate(
      {
        fonts: {
          heading: selectedPair.primaryFont,
          paragraph: selectedPair.secondaryFont,
          selectedPair: fontPairId
        }
      },
      { temporary: true }
    )
  }

  const handleSave = () => {
    const selectedPair = fontPairs.find((pair) => pair.id === selectedFontPair)
    onUpdate({
      fonts: {
        heading: selectedPair.primaryFont,
        paragraph: selectedPair.secondaryFont,
        selectedPair: selectedFontPair
      }
    })
    onClose() // Close the form after saving
  }

  const handleCancelClick = () => {
    // Reset to the stored font when closing without saving
    const storedFontPairId = findFontPairId(theme?.fonts?.heading, theme?.fonts?.paragraph)
    if (selectedFontPair !== storedFontPairId) {
      const storedPair = fontPairs.find((pair) => pair.id === storedFontPairId)
      onUpdate(
        {
          fonts: {
            heading: storedPair.primaryFont,
            paragraph: storedPair.secondaryFont,
            selectedPair: storedFontPairId
          }
        },
        { temporary: true }
      )
    }
    onClose()
  }

  return (
    <div className="w-full">
      {/* Font List */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-3">
          {fontPairs.map((fontPair) => {
            const isSelected = selectedFontPair === fontPair.id

            return (
              <div
                key={fontPair.id}
                className={`rounded-lg border-2 p-4 transition-all ${
                  isSelected ? "border-teal bg-teal/5" : "border-gray-light bg-white shadow-sm hover:shadow-md"
                }`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div
                      className="text-lg font-bold text-gray-dark"
                      style={{
                        fontFamily: formatFontFamily(fontPair.primaryFont),
                        fontWeight: 700
                      }}>
                      {fontPair.primary}
                    </div>
                    <div
                      className="text-sm text-gray"
                      style={{
                        fontFamily: formatFontFamily(fontPair.secondaryFont),
                        fontWeight: fontPair.secondaryFont.includes("Raleway") ? 300 : 400
                      }}>
                      {fontPair.secondary}
                    </div>
                  </div>

                  {isSelected ? (
                    <div className="flex items-center space-x-2">
                      <CheckIcon className="h-5 w-5 text-teal" />
                      <span className="text-sm font-medium text-teal">Selected</span>
                    </div>
                  ) : (
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleFontSelect(fontPair.id)}
                      className="flex items-center justify-center px-4 py-2 text-center">
                      Select
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 z-10 border-t border-gray-light bg-white px-6 py-4">
        <div className="flex items-center justify-end space-x-3">
          <Button type="secondary" onClick={handleCancelClick} className="px-6">
            Close
          </Button>
          <Button type="primary" onClick={handleSave} className="px-6">
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}

export default FontsForm
